import { SystemRoutes } from "./RouteUtils";

const StorageType = Object.freeze({
    SESSION:'SESSION',
    LOCAL:  'LOCAL'
});

/**
 * Get either localStorage or sessionStorage
 * @param type storage type
 */
export const getStorage = (type) => {
    if (type === StorageType.SESSION) {
        return window.sessionStorage;
    }
    return window.localStorage;
};

/**
 * Set an item into storage
 * @param type storage type
 * @param key key to set
 * @param value value to set
 */
const setItem = (type) => (key, value) => {
    getStorage(type).setItem(key, JSON.stringify(value));
};

/**
 * Get an item from storage
 * @param type storage type
 * @param key key to get
 * @param defaultVal value to return if key doesnt exist
 */
const getItem = (type) => (key, defaultVal) => {
    const val = getStorage(type).getItem(key);
    if (!val || val === 'undefined') return defaultVal;
    try {
        return JSON.parse(val);
    } catch (e) {
        return val;
    }
};

/**
 * Remove item from storage
 * @param type storage type
 * @param key key to remove
 */
const removeItem = (type) => (key) => {
    getStorage(type).removeItem(key);
};

/**
 * Clear all items from storage
 * @param type storage type
 */
const clear = (type) => () => {
    getStorage(type).clear();
};

/**
 * Clear all cookies from the current domain
 */
const clearAllCookies = () => {
    document.cookie.split(";").forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
    });
};

/**
 * Clear IndexedDB databases
 */
const clearIndexedDB = async () => {
    if ('indexedDB' in window) {
        try {
            const databases = await indexedDB.databases();
            await Promise.all(
                databases.map(db => {
                    return new Promise((resolve, reject) => {
                        const deleteReq = indexedDB.deleteDatabase(db.name);
                        deleteReq.onsuccess = () => resolve();
                        deleteReq.onerror = () => reject(deleteReq.error);
                    });
                })
            );
        } catch (error) {
            console.error('Error clearing IndexedDB:', error);
        }
    }
};

/**
 * Clear cache storage
 */
const clearCacheStorage = async () => {
    if ('caches' in window) {
        try {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
        } catch (error) {
            console.error('Error clearing cache storage:', error);
        }
    }
};

/**
 * Clear all site data (equivalent to "Clear site data" button)
 */
const clearAllSiteData = async () => {
    try {
        // Clear localStorage and sessionStorage
        localStorage.clear();
        sessionStorage.clear();

        // Clear cookies
        clearAllCookies();

        // Clear IndexedDB
        await clearIndexedDB();

        // Clear cache storage
        await clearCacheStorage();

        console.log('All site data cleared successfully');
    } catch (error) {
        console.error('Error clearing site data:', error);
    }
};

const clearPersistKey = (key = 'persist:root') => {
    try {
        console.log('Clearing persist key:', key);
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    } catch (e) {
        console.warn('clearPersistKey failed', e);
    }
};


/**
 * Clear all site data but preserve specific keys
 * @param preserveKeys array of keys to preserve in localStorage
//  * @param preserveSessionKeys array of keys to preserve in sessionStorage
//  */
const clearAllSiteDataExcept = async (preserveKeys = [], preserveSessionKeys = [], navigate) => {
    try {
        // Backup data to preserve
        const localBackup = {};
        const sessionBackup = {};

        // Backup localStorage items
        preserveKeys.forEach(key => {
            const value = localStorage.getItem(key);
            if (value !== null) {
                localBackup[key] = value;
            }
        });

        // Backup sessionStorage items
        preserveSessionKeys.forEach(key => {
            const value = sessionStorage.getItem(key);
            if (value !== null) {
                sessionBackup[key] = value;
            }
        });

        // Clear all data
        localStorage.clear();
        sessionStorage.clear();
        clearAllCookies();
        await clearIndexedDB();
        await clearCacheStorage();

        // Restore preserved data
        Object.keys(localBackup).forEach(key => {
            localStorage.setItem(key, localBackup[key]);
        });

        Object.keys(sessionBackup).forEach(key => {
            sessionStorage.setItem(key, sessionBackup[key]);
        });

        console.log('Site data cleared except preserved keys:', {
            localStorage: Object.keys(localBackup),
            sessionStorage: Object.keys(sessionBackup)
        });
        navigate(SystemRoutes.DASHBOARD_OVERVIEW);
        window.location.reload();
    } catch (error) {
        console.error('Error clearing site data with exceptions:', error);
    }
};

/**
 * Clear localStorage except for specific keys
 * @param preserveKeys array of keys to preserve
 */
const clearLocalStorageExcept = (preserveKeys = []) => {
    const backup = {};

    // Backup items to preserve
    preserveKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value !== null) {
            backup[key] = value;
        }
    });

    // Clear all
    localStorage.clear();

    // Restore preserved items
    Object.keys(backup).forEach(key => {
        localStorage.setItem(key, backup[key]);
    });
};

/**
 * Clear cookies except for specific names
 * @param preserveCookies array of cookie names to preserve
 */
const clearCookiesExcept = (preserveCookies = []) => {
    const backup = {};

    // Backup cookies to preserve
    preserveCookies.forEach(name => {
        const value = getCookie(name);
        if (value) {
            backup[name] = value;
        }
    });

    // Clear all cookies
    clearAllCookies();

    // Restore preserved cookies
    Object.keys(backup).forEach(name => {
        document.cookie = `${name}=${backup[name]};path=/`;
    });
};

/**
 * Get cookie value by name
 */
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

export const Storage = {
    session: {
        get: getItem(StorageType.SESSION),
        set: setItem(StorageType.SESSION),
        remove: removeItem(StorageType.SESSION),
        clear: clear(StorageType.SESSION),
    },
    local: {
        get: getItem(StorageType.LOCAL),
        set: setItem(StorageType.LOCAL),
        remove: removeItem(StorageType.LOCAL),
        clear: clear(StorageType.LOCAL),
    },
    // Utility functions for complete data clearing
    clearAllCookies,
    clearIndexedDB,
    clearCacheStorage,
    clearAllSiteData,
    // New functions that preserve specific data
    clearAllSiteDataExcept,
    clearLocalStorageExcept,
    clearCookiesExcept,
    getCookie,
    clearPersistKey,
};
